import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton , ArrowDown, ArrowUp } from '@coolremote/shared';
import useStyles from './styles';
import { t } from 'ttag';
import clsx from 'clsx';


/**
 * Validates whether a given value falls within a specified range.
 *
 * @param {number} newValue - The value to validate.
 * @param {number} maxValue - The maximum allowable value.
 * @param {number} minValue - The minimum allowable value.
 * @returns {boolean} Returns `true` if the value is within the range, otherwise `false`.
 */
const validateValue = (newValue,maxValue,minValue) => {
  if(isNaN(newValue)) {
    return false;
  }

  if (newValue === '') {
    return true;
  }

  return newValue >= minValue && newValue <= maxValue;
};


/**
 * Rounds a number to the nearest step.
 * Ensures the result has only one digit after the decimal point.
 * @param {number} value - The value to round.
 * @param {number} step - The step size to round to (e.g., 0.5).
 * @returns {number} The rounded value.
 */
const roundToStep = (value, step) => {
  const rounded = step <= 0 ? value : Math.round(value / step) * step;
  return Number(rounded.toFixed(1));
};

/**
 * NumericValue component allows incrementing and decrementing a numeric value within specified bounds.
 *
 * @param {Object} props - Component properties.
 * @param {number} props.value - The current numeric value.
 * @param {number} props.max - The maximum allowable value.
 * @param {number} props.min - The minimum allowable value.
 * @param {boolean} props.isDisabled - Whether the component is disabled.
 * @param {number} props.step - Step value for increment/decrement (default: 1)
 * @param {function} props.onChange - Callback function when the value changes.
 * @returns {JSX.Element} The rendered component.
 */
const NumericValue = ({
  value,
  max,
  min,
  isDisabled,
  step,
  onChange,
  symbol,
  textSize,
  textColor,
}) => {
  const { container, button, input, inputWrapper,inputError:inputErrorStyle } = useStyles();

  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [inputError, setInputError] = useState('');
  const textFieldRef = useRef(null);

  // Check if value is a valid number
  const valueIsNumber = typeof value === 'number' && !isNaN(value);

  // If value is not a number, display 0 and disable the component
  const displayValue = valueIsNumber ? roundToStep(value, step) : 0;

  const componentDisabled = isDisabled || !valueIsNumber || !step;

  // Determine if the increment and decrement buttons should be disabled
  const disableUp = componentDisabled || displayValue >= max;
  const disableDown = componentDisabled || displayValue <= min;

  const style = {
    fontSize: textSize || 'inherit',
    color: textColor || 'inherit',
    fontWeight: 'bold',
    fontFamily: 'Roboto',
    lineHeight: '24.2px',
  };

  useEffect(() => {
    if(textFieldRef.current) {
      textFieldRef.current.focus();
      //setInputValue(displayValue);
    }

  },[textFieldRef.current]);

  /**
   * Handler for incrementing the value.
   */
  const handleUpClick = () => {
    if (!componentDisabled && displayValue < max) {
      const newValue = roundToStep(displayValue + step, step);
      onChange(newValue);
    }
  };

  /**
   * Handler for decrementing the value.
   */
  const handleDownClick = () => {
    if (!componentDisabled && displayValue > min) {
      const newValue = roundToStep(displayValue - step, step);
      onChange(newValue);
    }
  };

  const handleNumberClick = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    if(!validateValue(inputValue,max,min)) {
      setInputValue(displayValue);
    }else{
      onChange(inputValue);
    }
    setIsEditing(false);
  };


  const handleInputChange = ({target:{value}}) => {
    const newValue = value ? parseInt(value) : '';
    if(!validateValue(newValue,max,min)) {
      setInputError(t`value must be between ${min} and ${max}`);
    }else{
      setInputError('');
    }
    setInputValue(newValue);
  };

  // show the value and symbol if it exists
  const displayValueWithSymbol = symbol ? `${displayValue} ${symbol}` : displayValue;



  const renderValue = () => {
    if (isEditing) {
      return (
        <div className={inputWrapper} title={inputError}>
          <input className={clsx(input,{[inputErrorStyle]:inputError})} type="text" ref={textFieldRef} onBlur={handleSave} value={inputValue} onChange={handleInputChange} style={style} />
        </div>
      );
    }
    return <div className={inputWrapper} title={inputError}>
      <div style={style} onClick={handleNumberClick}>{displayValueWithSymbol}</div>
    </div>;
  };

  return (
    <div className={container}>
      <IconButton
        color="primary"
        className={button}
        onClick={handleDownClick}
        disabled={disableDown}
      >
        <ArrowDown color={textColor}/>
      </IconButton>
      {renderValue()}

      <IconButton
        color="primary"
        className={button}
        onClick={handleUpClick}
        disabled={disableUp}
      >
        <ArrowUp color={textColor} />
      </IconButton>
    </div>
  );
};

NumericValue.propTypes = {
  value: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  isDisabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  step: PropTypes.number,
};

NumericValue.defaultProps = {
  isDisabled: false,
};

export default NumericValue;
